import { queryOptions } from '@tanstack/react-query';
import { qk } from './query-client';
import { getCourses, getProductCodes } from './reference-data';
import { Network } from './types';

/**
 * The network id every `network/{id}/…` route takes, and the shared queries
 * built on it (FRA-20261025-001).
 *
 * That path segment is `networks.id`. It used to be filled with the HQ
 * company id, which worked only because api-v1 ignored the segment — and was
 * indistinguishable locally because network 1 is headed by company 1. api-v1
 * is about to enforce it as a network the caller heads (API-20261025-003), at
 * which point a company id breaks every network whose ids differ.
 *
 * `GET network` is the single source of network identity (agreed in the
 * course-info-tab handoff); `AppContext` exposes the result as `networkId`.
 * It is null until that fetch lands, and stays null if it fails — so these
 * queries are DISABLED rather than sent to `network/undefined/…` or
 * `network/0/…`.
 */
export function resolveNetworkId(network: Pick<Network, 'id'> | null | undefined): number | null {
    const id = Number(network?.id);
    return Number.isInteger(id) && id > 0 ? id : null;
}

// Reference data: it does not change while someone reads a report.
const REFERENCE_STALE_TIME = 10 * 60_000;

// Keyed by network, not company: requesting by one and caching by the other
// would let a network's list serve another after a company switch. The `0` in
// the unresolved key is never fetched — `enabled` is false.
export function productCodesQuery(networkId: number | null) {
    return queryOptions({
        queryKey: qk.referenceData.productCodes(networkId ?? 0),
        queryFn: ({ signal }) => getProductCodes(networkId!, signal),
        enabled: networkId !== null,
        staleTime: REFERENCE_STALE_TIME,
    });
}

export function coursesQuery(networkId: number | null) {
    return queryOptions({
        queryKey: qk.referenceData.courses(networkId ?? 0),
        queryFn: ({ signal }) => getCourses(networkId!, signal),
        enabled: networkId !== null,
        staleTime: REFERENCE_STALE_TIME,
    });
}
